import { CAR_FLOW_TYPE } from '~/data/car/carCardData';
import { CarCardType, SimilarFamilyCar } from '~/data/types/car/cars';
import { EUrlParam } from '~/data/urlParams';
import { useMemo } from 'react';
import { useCurrentUrlParams } from '~/hooks/useCurrentUrlParams';
import { useGetNewCarRoute } from '~/hooks/useGetNewCarRoute';
import { useIsLeaseCar } from '~/hooks/useIsLeaseCar';
import { useSelector } from 'react-redux';
import { RootState } from '~/redux-toolkit/store/store';
import { getKeyByValue } from '~/utils/populateFiltersFromQuery';

export const useCarPageUrl = (
  car: CarCardType | SimilarFamilyCar,
  dealType: CAR_FLOW_TYPE,
  preserveKeys?: string[],
) => {
  const currentUrlParams = useCurrentUrlParams({
    cleanAppParams: true,
    preserveKeys: preserveKeys,
  });

  const isLease = useIsLeaseCar();
  const carPageRoute = useGetNewCarRoute(
    dealType === CAR_FLOW_TYPE.LEASING_CAR,
  );

  const stateType = useMemo(
    () => (isLease ? 'resultPageLease' : 'resultPage'),
    [isLease],
  );

  const { manufacturerEnum } = useSelector(
    (state: RootState) => state[stateType],
  );

  return useMemo(() => {
    currentUrlParams.set(EUrlParam.DEAL_TYPE, dealType);
    const paramsString = currentUrlParams.toString();
    const queryParams = paramsString ? `?${paramsString}` : '';

    const manufacturerDisplayName = getKeyByValue(
      manufacturerEnum,
      car?.manufacturerDisplayName?.toLocaleLowerCase()?.trim() as string,
    );

    const makeName =
      manufacturerDisplayName ||
      car?.carZoneMakeNameEn?.toLocaleLowerCase() ||
      'unknown';

    const modelName =
      car?.modelNameDisplayName?.replace(/\s+/g, '-').toLocaleLowerCase() ||
      car?.dmvModelName?.replace(/\s+/g, '-').toLocaleLowerCase() ||
      car?.carZoneModelNameEn?.replace(/\s+/g, '-').toLocaleLowerCase() ||
      'unknown';
    const year = car.year || 'unknown';
    const carId = car.id || 'unknown';

    return `${carPageRoute}/${makeName}/${modelName}/${year}-c${carId}${queryParams}`;
  }, [
    currentUrlParams,
    dealType,
    car?.year,
    car?.carZoneMakeNameEn,
    car?.carZoneModelNameEn,
    car?.id,
    car?.dmvModelName,
    car?.manufacturerDisplayName,
    car?.modelNameDisplayName,
    carPageRoute,
    manufacturerEnum,
  ]);
};
